import { useRef, useState } from "react";
import { Seo } from "@/components/Seo";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import MathAssessments from "@/components/site/MathAssessments";
import TutoringShowcase from "@/components/site/TutoringShowcase";
import GiftLearning from "@/components/site/GiftLearning";

const HIGHLIGHTS = [
  { k: "1:1 sessions", v: "Personal attention, real progress" },
  { k: "Adult learners", v: "Judgment-free, results-driven" },
  { k: "Nursing focused", v: "Dosage & clinical math specialty" },
];

export default function MathTutoringPage() {
  const vref = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const navigate = useNavigate();

  const togglePlay = () => {
    const v = vref.current; if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); } else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = vref.current; if (!v) return;
    v.muted = !v.muted; setMuted(v.muted);
  };

  return (
    <main data-testid="page-math-tutoring" className="bg-[#050505] px-6 md:px-12 pt-32 pb-24">
      <Seo
        title="Math Tutoring with Dr. Dennis Louden — Delta DL Enterprise, LLC"
        description="One-on-one online math tutoring — algebra made simple, step by step. Book a session with Dr. Dennis Louden."
        path="/math-tutoring"
      />
      <div className="mx-auto max-w-[1400px]">
        <motion.div initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.8, ease:[0.16,1,0.3,1]}}>
          <span className="text-xs uppercase tracking-[0.4em] text-[#7C3AED]">One-on-One</span>
          <h1 className="mt-4 font-display text-6xl md:text-8xl uppercase tracking-tight text-white">Math Tutoring</h1>
          <p className="mt-6 max-w-2xl text-lg text-white/60">
            Personalized algebra tutoring with Dr. Dennis D. Louden — designed for adult
            learners, nursing students, and anyone who wants to master the mathematics of
            their field with confidence.
          </p>
        </motion.div>

        <motion.div initial={{opacity:0,y:30}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.9,delay:0.15}}
          className="mt-10 relative overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(124,58,237,0.35)]">
          <video ref={vref} data-testid="math-video" className="w-full aspect-video object-cover" autoPlay muted loop playsInline preload="auto">
            <source src="/math-tutoring.mp4" type="video/mp4" />
            <source src="/hero.mp4" type="video/mp4" />
          </video>
          <div className="absolute bottom-4 left-4 flex gap-2">
            <button data-testid="math-play-btn" onClick={togglePlay} aria-label={playing?"Pause":"Play"} className="rounded-full bg-black/70 backdrop-blur px-3 py-2 text-white hover:bg-[#7C3AED] transition-colors">
              {playing ? <Pause size={18}/> : <Play size={18}/>}
            </button>
            <button data-testid="math-mute-btn" onClick={toggleMute} aria-label={muted?"Unmute":"Mute"} className="rounded-full bg-black/70 backdrop-blur px-3 py-2 text-white hover:bg-[#7C3AED] transition-colors">
              {muted ? <VolumeX size={18}/> : <Volume2 size={18}/>}
            </button>
          </div>
        </motion.div>

        <TutoringShowcase />

        <section data-testid="promo-clip" className="mt-14">
          <span className="text-xs uppercase tracking-[0.4em] text-[#A78BFA]">Share the Word</span>
          <h2 className="mt-3 font-display text-4xl md:text-6xl uppercase tracking-tight text-white">
            Promo Clips
          </h2>
          <p className="mt-4 max-w-2xl text-base text-white/60">
            Two 23-second promos for your classes — play them here, or download and share them
            anywhere: text messages, social media, or email.
          </p>
          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest text-white/70">Math Tutoring</h3>
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(124,58,237,0.35)]">
                <video
                  data-testid="promo-video"
                  src="/tutoring-promo.mp4"
                  controls
                  playsInline
                  preload="metadata"
                  className="aspect-video w-full"
                />
              </div>
              <a
                href="/tutoring-promo.mp4"
                download="Delta-DL-Enterprise-Tutoring-Promo.mp4"
                data-testid="promo-download"
                className="magnetic-btn mt-4 inline-flex items-center gap-2 rounded-full border border-[#C9A227]/60 px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#C9A227] transition-colors hover:bg-[#C9A227] hover:text-black"
              >
                Download the Clip
              </a>
            </div>
            <div>
              <h3 className="mb-3 text-sm font-semibold uppercase tracking-widest text-white/70">Nursing Math</h3>
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-black shadow-[0_20px_80px_-20px_rgba(45,190,175,0.35)]">
                <video
                  data-testid="nursing-promo-video"
                  src="/nursing-promo.mp4"
                  controls
                  playsInline
                  preload="metadata"
                  className="aspect-video w-full"
                />
              </div>
              <a
                href="/nursing-promo.mp4"
                download="Delta-DL-Enterprise-Nursing-Math-Promo.mp4"
                data-testid="nursing-promo-download"
                className="magnetic-btn mt-4 inline-flex items-center gap-2 rounded-full border border-[#2DBEAF]/60 px-6 py-3 text-xs font-bold uppercase tracking-widest text-[#2DBEAF] transition-colors hover:bg-[#2DBEAF] hover:text-black"
              >
                Download the Clip
              </a>
            </div>
          </div>
        </section>

        <MathAssessments />

        <GiftLearning />

        <div className="mt-14 grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
          {HIGHLIGHTS.map((h) => (
            <div key={h.k} className="bg-[#050505] px-6 py-8">
              <div className="font-display text-2xl uppercase text-white">{h.k}</div>
              <div className="mt-2 text-white/60">{h.v}</div>
            </div>
          ))}
        </div>

        <div data-testid="math-price" className="mt-14 grid gap-6 md:grid-cols-2">
          <div className="flex flex-wrap items-center gap-x-8 gap-y-4 rounded-2xl border border-[#7C3AED]/30 bg-[#7C3AED]/10 p-8">
            <div>
              <div className="font-display text-5xl text-white">$50</div>
              <div className="mt-1 text-xs uppercase tracking-[0.25em] text-white/60">Per 1-hour online session</div>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-white/60">
              Pay per session — no packages, no contracts.
            </p>
          </div>
          <div data-testid="math-bundle" className="flex flex-wrap items-center gap-x-8 gap-y-4 rounded-2xl border border-white/10 bg-[#0D0D12] p-8">
            <div>
              <div className="font-display text-5xl text-white">$225</div>
              <div className="mt-1 text-xs uppercase tracking-[0.25em] text-white/60">5-session bundle — save $25</div>
            </div>
            <p className="max-w-xs text-sm leading-relaxed text-white/60">
              Best value — lock in five sessions and build real momentum.
            </p>
          </div>
        </div>
        <p className="mt-4 text-xs text-white/40">
          Your schedule and bundle choice are confirmed personally by email after you apply.
        </p>

        <div className="mt-14 flex flex-wrap gap-4">
          <button onClick={() => navigate("/enroll?course=Math Tutoring")} data-testid="math-book-btn" className="magnetic-btn rounded-full bg-[#7C3AED] px-8 py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-white hover:text-black">
            Book a Session
          </button>
          <a href="mailto:Deltadlenterprise2026@gmail.com" data-testid="math-email-link" className="magnetic-btn rounded-full border border-white/30 px-8 py-3 text-sm uppercase tracking-wider text-white hover:bg-white hover:text-black">
            Ask a Question
          </a>
        </div>
      </div>
    </main>
  );
}
